import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import MultiSelectDropdown from "./MultiSelectDropdown";
import { Pin, PinOff } from "lucide-react";
import StatusSortDialog from "../Dialogs/StatusSortDialog";
import { useTableContext } from "../../hooks/useTableContext";

/**
 * Enhanced column menu dropdown that includes filtering functionality
 */
const ColumnMenuDropdown = ({
  isOpen,
  onClose,
  hasFilters = false,
  filterProps = {},
  columnId,
}) => {
  const dropdownRef = useRef(null);
  const dialogRef = useRef(null);
  const [anchorLeft, setAnchorLeft] = useState(false);
  const [tempSelectedFilters, setTempSelectedFilters] = useState([]);
  const [openStatusSortDialog, setOpenStatusSortDialog] = useState(false);

  const {
    state,
    setSorting,
    togglePinnedColumn,
    setColumnVisibility,
    setFilters,
  } = useTableContext();
  const currentTableSort =
    state.sorting && state.sorting.length > 0 ? state.sorting[0] : null;
  const isColumnPinned = state.pinnedColumns?.includes(columnId);

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!isOpen || !onClose) return;
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        (!dialogRef.current || !dialogRef.current.contains(event.target))
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // When opened, check if it's off-screen to the left
  useLayoutEffect(() => {
    if (isOpen && dropdownRef.current) {
      const { left } = dropdownRef.current.getBoundingClientRect();
      setAnchorLeft(left < 0);
    }
  }, [isOpen]);

  // Handler for MultiSelectDropdown's onSelectedChange (intermediate changes)
  const handleTempFilterChange = (newValues) => {
    setTempSelectedFilters(newValues);
  };

  // Handler for MultiSelectDropdown's onApply (confirming filter changes)
  const applyFiltersFromMultiSelect = (confirmedValues) => {
    const ids = confirmedValues.map((opt) =>
      typeof opt === "object" ? opt.id ?? opt.value ?? String(opt) : String(opt)
    );
    setFilters({ [columnId]: ids });
    onClose();
  };

  if (!isOpen) return null;

  const isSortedAsc =
    currentTableSort &&
    currentTableSort.id === columnId &&
    !currentTableSort.desc;
  const isSortedDesc =
    currentTableSort &&
    currentTableSort.id === columnId &&
    currentTableSort.desc;

  const handleSortClick = (desc) => {
    setSorting([{ id: columnId, desc }]);
    onClose();
  };

  const handlePinClick = () => {
    togglePinnedColumn(columnId);
    onClose();
  };

  const handleHideClick = () => {
    setColumnVisibility((prev) => ({ ...prev, [columnId]: false }));
    onClose();
  };

  return (
    <div
      ref={dropdownRef}
      className={`absolute ${anchorLeft ? "left-0" : "right-0"} top-10
        bg-white shadow-lg rounded-lg z-50 w-64 border border-gray-200`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Sorting Section */}
      <div className="p-2.5">
        <div className="flex gap-1">
          {/* Sort A to Z */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSortClick(false);
            }}
            className={`flex flex-col items-center w-full py-2 rounded transition-colors ${
              isSortedAsc ? "bg-[#E3EAF9]" : "bg-[#F4F7FE] hover:bg-[#E3EAF9]"
            }`}
            style={{ position: "relative", zIndex: 60, cursor: "pointer" }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.49989 11H4.99989V1.5C4.99989 1.36739 4.94721 1.24021 4.85344 1.14645C4.75967 1.05268 4.63249 1 4.49989 1H3.49989C3.36728 1 3.2401 1.05268 3.14633 1.14645C3.05256 1.24021 2.99989 1.36739 2.99989 1.5V11H1.49989C1.05645 11 0.832385 11.5387 1.14707 11.8534L3.64707 14.8534C3.74083 14.9471 3.86796 14.9998 4.00051 14.9998C4.13306 14.9998 4.26019 14.9471 4.35395 14.8534L6.85395 11.8534C7.16707 11.5394 6.94426 11 6.49989 11ZM13.9999 9H9.99989C9.86728 9 9.7401 9.05268 9.64633 9.14645C9.55256 9.24021 9.49989 9.36739 9.49989 9.5V10.5C9.49989 10.6326 9.55256 10.7598 9.64633 10.8536C9.7401 10.9473 9.86728 11 9.99989 11H11.7499L9.83551 13.2016C9.72995 13.2954 9.64547 13.4105 9.58762 13.5394C9.52977 13.6682 9.49987 13.8078 9.49989 13.9491V14.5C9.49989 14.6326 9.55256 14.7598 9.64633 14.8536C9.7401 14.9473 9.86728 15 9.99989 15H13.9999C14.1325 15 14.2597 14.9473 14.3534 14.8536C14.4472 14.7598 14.4999 14.6326 14.4999 14.5V13.5C14.4999 13.3674 14.4472 13.2402 14.3534 13.1464C14.2597 13.0527 14.1325 13 13.9999 13H12.2499L14.1643 10.7984C14.2698 10.7046 14.3543 10.5895 14.4121 10.4606C14.47 10.3318 14.4999 10.1922 14.4999 10.0509V9.5C14.4999 9.36739 14.4472 9.24021 14.3534 9.14645C14.2597 9.05268 14.1325 9 13.9999 9ZM14.9705 6.33187L13.1183 1.33187C13.0836 1.23474 13.0198 1.1507 12.9355 1.09129C12.8511 1.03188 12.7505 0.999989 12.6474 1H11.3524C11.2492 0.999989 11.1486 1.03188 11.0643 1.09129C10.98 1.1507 10.9161 1.23474 10.8814 1.33187L9.02926 6.33187C9.00233 6.40731 8.99391 6.48811 9.00472 6.56748C9.01553 6.64684 9.04525 6.72245 9.09138 6.78794C9.1375 6.85342 9.19869 6.90686 9.26978 6.94377C9.34087 6.98067 9.41979 6.99996 9.49989 7H10.2758C10.3816 7.00003 10.4846 6.96654 10.5701 6.90432C10.6557 6.84211 10.7192 6.75438 10.7518 6.65375L10.8899 6.25H13.1086L13.2464 6.65375C13.279 6.75458 13.3428 6.84246 13.4286 6.90469C13.5143 6.96692 13.6177 7.0003 13.7236 7H14.4999C14.58 6.99996 14.6589 6.98067 14.73 6.94377C14.8011 6.90686 14.8623 6.85342 14.9084 6.78794C14.9545 6.72245 14.9842 6.64684 14.9951 6.56748C15.0059 6.48811 14.9974 6.40731 14.9705 6.33187ZM11.4877 4.5L11.9999 3L12.5121 4.5H11.4877Z"
                fill="#2B3674"
              />
            </svg>
          </button>
          {/* Sort Z to A */}
          <button
            onClick={() => {
              handleSortClick(true);
            }}
            className={`flex flex-col items-center w-full py-2 rounded transition-colors ${
              isSortedDesc ? "bg-[#E3EAF9]" : "bg-[#F4F7FE] hover:bg-[#E3EAF9]"
            }`}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.50014 5H3.00014V14.5C3.00014 14.6326 3.05282 14.7598 3.14659 14.8536C3.24035 14.9473 3.36753 15 3.50014 15H4.50014C4.63275 15 4.75992 14.9473 4.85369 14.8536C4.94746 14.7598 5.00014 14.6326 5.00014 14.5V5H6.50014C6.9442 5 7.16826 4.46125 6.85358 4.14656L4.35358 1.14656C4.25982 1.05287 4.13269 1.00023 4.00014 1.00023C3.86759 1.00023 3.74046 1.05287 3.6467 1.14656L1.1467 4.14656C0.832952 4.46063 1.05576 5 1.50014 5ZM14.0001 9H10.0001C9.86753 9 9.74035 9.05268 9.64659 9.14645C9.55282 9.24021 9.50014 9.36739 9.50014 9.5V10.5C9.50014 10.6326 9.55282 10.7598 9.64659 10.8536C9.74035 10.9473 9.86753 11 10.0001 11H11.7501L9.83576 13.2016C9.73021 13.2954 9.64572 13.4105 9.58788 13.5394C9.53003 13.6682 9.50012 13.8078 9.50014 13.9491V14.5C9.50014 14.6326 9.55282 14.7598 9.64659 14.8536C9.74035 14.9473 9.86753 15 10.0001 15H14.0001C14.1327 15 14.2599 14.9473 14.3537 14.8536C14.4475 14.7598 14.5001 14.6326 14.5001 14.5V13.5C14.5001 13.3674 14.4475 13.2402 14.3537 13.1464C14.2599 13.0527 14.1327 13 14.0001 13H12.2501L14.1645 10.7984C14.2701 10.7046 14.3546 10.5895 14.4124 10.4606C14.4703 10.3318 14.5002 10.1922 14.5001 10.0509V9.5C14.5001 9.36739 14.4475 9.24021 14.3537 9.14645C14.2599 9.05268 14.1327 9 14.0001 9ZM14.9708 6.33187L13.1186 1.33187C13.0839 1.23474 13.02 1.1507 12.9357 1.09129C12.8514 1.03188 12.7508 0.999989 12.6476 1H11.3526C11.2495 0.999989 11.1489 1.03188 11.0646 1.09129C10.9803 1.1507 10.9164 1.23474 10.8817 1.33187L9.02951 6.33187C9.00258 6.40731 8.99416 6.48811 9.00497 6.56748C9.01578 6.64684 9.0455 6.72245 9.09163 6.78794C9.13776 6.85342 9.19894 6.90686 9.27003 6.94377C9.34112 6.98067 9.42004 6.99996 9.50014 7H10.2761C10.3818 7.00003 10.4849 6.96654 10.5704 6.90432C10.6559 6.84211 10.7195 6.75438 10.752 6.65375L10.8901 6.25H13.1089L13.2467 6.65375C13.2793 6.75458 13.3431 6.84246 13.4288 6.90469C13.5146 6.96692 13.6179 7.0003 13.7239 7H14.5001C14.5802 6.99996 14.6592 6.98067 14.7302 6.94377C14.8013 6.90686 14.8625 6.85342 14.9086 6.78794C14.9548 6.72245 14.9845 6.64684 14.9953 6.56748C15.0061 6.48811 14.9977 6.40731 14.9708 6.33187ZM11.488 4.5L12.0001 3L12.5123 4.5H11.488Z"
                fill="#2B3674"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Column Operations */}
      <div className="px-2.5 gap-y-2.5">
        {/* Pin Column button */}
        <button
          onClick={handlePinClick}
          className="flex gap-2 items-center w-full px-2.5 py-1.5 hover:bg-[#EAEFF3] rounded mb-2.5"
        >
          {!isColumnPinned ? (
            <Pin className="stroke-[#2B3674] w-4 h-4" />
          ) : (
            <PinOff className="stroke-[#2B3674] w-4 h-4" />
          )}
          <span className="text-sm text-[#2B3674]">
            {isColumnPinned ? "Открепить колонку" : "Закрепить колонку"}
          </span>
        </button>

        {/* Hide Column button */}
        <button
          onClick={handleHideClick}
          className="flex items-center w-full px-2.5 py-1.5 hover:bg-[#EAEFF3] rounded mb-2.5"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="mr-2"
          >
            <g clipPath="url(#clip0_15781_103174)">
              <path
                d="M2.66366 2.66797L13.3303 13.3346M10.997 11.1719C10.0953 11.6576 9.07607 12.0012 7.997 12.0012C5.64323 12.0012 3.57456 10.3665 2.38827 9.18717C2.07514 8.8759 1.9186 8.72024 1.81894 8.4147C1.74786 8.19684 1.74784 7.8057 1.81894 7.58777C1.91862 7.2823 2.07549 7.1263 2.38925 6.81444C2.98717 6.22011 3.80908 5.51036 4.77853 4.95248M12.997 9.75697C13.219 9.56164 13.4224 9.3695 13.6049 9.18817L13.6068 9.1861C13.9192 8.87557 14.0759 8.7199 14.1753 8.41503C14.2464 8.19717 14.2463 7.80584 14.1752 7.58797C14.0756 7.28257 13.9189 7.12644 13.6059 6.81537C12.4196 5.63602 10.3507 4.0013 7.997 4.0013C7.772 4.0013 7.5496 4.01624 7.33034 4.04429M8.87894 9.0013C8.64387 9.20877 8.33514 9.33464 7.997 9.33464C7.2606 9.33464 6.66366 8.7377 6.66366 8.0013C6.66366 7.64164 6.80607 7.31537 7.0376 7.0755"
                stroke="#2B3674"
                strokeWidth="1.33333"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_15781_103174">
                <rect width="16" height="16" fill="white" />
              </clipPath>
            </defs>
          </svg>
          <span className="text-sm text-[#2B3674]">Скрыть колонку</span>
        </button>
      </div>

      {/* Filter Section using the revamped MultiSelectDropdown */}
      {hasFilters && (
        <div className="p-3 border-t border-gray-100 mt-2">
          <MultiSelectDropdown
            options={filterProps.options || []}
            selectedOptions={tempSelectedFilters}
            onSelectedChange={handleTempFilterChange}
            onApply={applyFiltersFromMultiSelect}
            onClose={onClose}
            placeholder="Выберите значения для фильтра"
            columnId={columnId}
            resetText="Сбросить выбор"
          />
        </div>
      )}

      {columnId === "status" && (
        <div className="px-2.5 gap-y-2.5">
          <button
            className="flex text-sm items-center w-full gap-2 px-2.5 py-1.5 hover:bg-[#EAEFF3] rounded mb-2.5"
            onClick={(e) => {
              e.stopPropagation();
              setOpenStatusSortDialog(!openStatusSortDialog);
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.53073 7.63636H10.7974C10.9388 7.63636 11.0745 7.5789 11.1745 7.4766C11.2745 7.37431 11.3307 7.23557 11.3307 7.09091V4.90909C11.3307 4.76443 11.2745 4.62569 11.1745 4.5234C11.0745 4.4211 10.9388 4.36364 10.7974 4.36364H6.53073C6.38928 4.36364 6.25362 4.4211 6.15361 4.5234C6.05359 4.62569 5.9974 4.76443 5.9974 4.90909V5.45455H3.86406V3.27273H5.46406C5.60551 3.27273 5.74117 3.21526 5.84119 3.11297C5.94121 3.01067 5.9974 2.87194 5.9974 2.72727V0.545455C5.9974 0.400791 5.94121 0.262052 5.84119 0.15976C5.74117 0.0574673 5.60551 0 5.46406 0H1.1974C1.05595 0 0.920292 0.0574673 0.820272 0.15976C0.720253 0.262052 0.664063 0.400791 0.664062 0.545455V2.72727C0.664063 2.87194 0.720253 3.01067 0.820272 3.11297C0.920292 3.21526 1.05595 3.27273 1.1974 3.27273H2.7974V10.3636C2.7974 10.5083 2.85359 10.647 2.95361 10.7493C3.05362 10.8516 3.18928 10.9091 3.33073 10.9091H5.9974V11.4545C5.9974 11.5992 6.05359 11.7379 6.15361 11.8402C6.25362 11.9425 6.38928 12 6.53073 12H10.7974C10.9388 12 11.0745 11.9425 11.1745 11.8402C11.2745 11.7379 11.3307 11.5992 11.3307 11.4545V9.27273C11.3307 9.12806 11.2745 8.98933 11.1745 8.88703C11.0745 8.78474 10.9388 8.72727 10.7974 8.72727H6.53073C6.38928 8.72727 6.25362 8.78474 6.15361 8.88703C6.05359 8.98933 5.9974 9.12806 5.9974 9.27273V9.81818H3.86406V6.54545H5.9974V7.09091C5.9974 7.23557 6.05359 7.37431 6.15361 7.4766C6.25362 7.5789 6.38928 7.63636 6.53073 7.63636ZM7.06406 5.45455H10.2641V6.54545H7.06406V5.45455ZM1.73073 1.09091H4.93073V2.18182H1.73073V1.09091ZM7.06406 9.81818H10.2641V10.9091H7.06406V9.81818Z"
                fill="#2B3674"
              />
            </svg>
            <span className="text-sm text-[#2B3674]">Сортировать статусы</span>
          </button>
          <StatusSortDialog
            onClose={() => setOpenStatusSortDialog(false)}
            isOpen={openStatusSortDialog}
            dialogRef={dialogRef}
          />
        </div>
      )}
    </div>
  );
};

export default ColumnMenuDropdown;
